import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@ooh/screens';
import { notificationsEndpoints } from '@ooh/contracts';
import type { UnreadCountResponse } from '@ooh/contracts';
import { notificationKeys } from './queries';

// ─── Mark single notification as read ────────────────────────

interface MarkReadContext {
  previousUnreadCount: UnreadCountResponse | undefined;
}

/**
 * Mutation hook to mark a single notification as read.
 *
 * Uses optimistic update pattern:
 * - onMutate: decrement unread count in cache; save prior value for rollback
 * - onError: restore prior unread count from context
 * - onSuccess: invalidate unread count query to sync with server
 *
 * Traceability: reservation-reminder-in-app-notifications / spec.md
 */
export function useMarkNotificationRead() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const unreadKey = notificationKeys.unreadCount();
  const listKey = notificationKeys.lists();

  return useMutation<void, Error, number, MarkReadContext>({
    mutationFn: (id: number) => api.call(notificationsEndpoints.markRead, { id }),

    onMutate: async (_id: number): Promise<MarkReadContext> => {
      await queryClient.cancelQueries({ queryKey: unreadKey });
      const previousUnreadCount = queryClient.getQueryData<UnreadCountResponse>(unreadKey);

      queryClient.setQueryData(unreadKey, (old: UnreadCountResponse | undefined) => {
        if (!old) return old;
        return { unreadCount: Math.max(0, old.unreadCount - 1) };
      });

      return { previousUnreadCount };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousUnreadCount !== undefined) {
        queryClient.setQueryData(unreadKey, context.previousUnreadCount);
      }
    },

    onSuccess: () => {
      // Invalidate both the unread count and the list so the notification renders
      // with the updated readAt value (removes bold/accent background).
      void queryClient.invalidateQueries({ queryKey: unreadKey });
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
}

// ─── Mark all notifications as read ──────────────────────────

interface MarkAllReadContext {
  previousUnreadCount: UnreadCountResponse | undefined;
}

/**
 * Mutation hook to mark all notifications as read.
 *
 * Uses optimistic update pattern:
 * - onMutate: set unread count to 0; save prior value for rollback
 * - onError: restore prior unread count from context
 * - onSuccess: invalidate list and unread count queries to sync with server
 *
 * Traceability: reservation-reminder-in-app-notifications / spec.md
 */
export function useMarkAllNotificationsRead() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const unreadKey = notificationKeys.unreadCount();
  const listKey = notificationKeys.lists();

  return useMutation<void, Error, void, MarkAllReadContext>({
    mutationFn: () => api.call(notificationsEndpoints.markAllRead, undefined),

    onMutate: async (): Promise<MarkAllReadContext> => {
      await queryClient.cancelQueries({ queryKey: unreadKey });
      const previousUnreadCount = queryClient.getQueryData<UnreadCountResponse>(unreadKey);

      queryClient.setQueryData(unreadKey, { unreadCount: 0 });

      return { previousUnreadCount };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousUnreadCount !== undefined) {
        queryClient.setQueryData(unreadKey, context.previousUnreadCount);
      }
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listKey });
      void queryClient.invalidateQueries({ queryKey: unreadKey });
    },
  });
}
